import { css } from '@emotion/react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonNote,
  IonRow,
  IonTextarea,
} from '@ionic/react';
import { copyOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import * as commonCss from '../../styles/common';
import { Title } from '../Title';
import { openMapAddress } from '../../shared/openMap';
import { ToastMsg } from '../../layouts/ToastMsg';

function styleObjectToCSS(styleObj: object) {
  return Object.entries(styleObj)
    .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`)
    .join(' ');
}

export interface WidgetCreatorProps {
  iframeSrc: string;
  iframeTitle: string;
  initialWidth?: number;
  initialHeight?: number;
  initialBorder?: boolean;
  extraOptions?: React.ReactNode;
  updateIframeWhenSrcChanges?: boolean;
}

const WidgetCreator: React.FC<WidgetCreatorProps> = ({
  iframeSrc,
  iframeTitle,
  initialWidth = 300,
  initialHeight = 300,
  initialBorder = true,
  extraOptions = null,
  updateIframeWhenSrcChanges = true,
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const [iframeStyle, setIframeStyle] = useState<object>({});
  const [hasBorder, setHasBorder] = useState(initialBorder);
  const [snippet, setSnippet] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [initialIFrameSrc] = useState(iframeSrc);

  // Update snippet
  useEffect(() => {
    const style = styleObjectToCSS(iframeStyle);
    setSnippet(
      `<iframe src="${openMapAddress}/${iframeSrc}" width="${width}" height="${height}" title="${iframeTitle}"${style ? ` style="${style}"` : ''}></iframe>`
    );
  }, [iframeSrc, width, height, iframeStyle]);

  // Update style
  useEffect(() => {
    const newStyle = Object();
    if (hasBorder) {
      newStyle['border'] = '1px solid';
      newStyle['borderRadius'] = '5px';
    } else {
      newStyle['border'] = 'none';
    }
    setIframeStyle(newStyle);
  }, [hasBorder]);

  const widgetCreatorCss = {
    snippetTextarea: [
      commonCss.smallText,
      css({
        fontFamily: 'monospace',
      }),
    ],
    previewSection: [
      css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }),
    ],
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setShowToast(true);
    });
  };

  return (
    <IonCard>
      <IonCardContent>
        <IonGrid>
          <IonRow>
            <IonCol sizeXs="12" sizeXl="6">
              <IonItem lines="none">
                <Title>Configuration</Title>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Width</IonLabel>
                <IonInput inputMode="numeric" value={width} onIonChange={(e) => setWidth(parseInt(e.detail.value!))} />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Height</IonLabel>
                <IonInput value={height} onIonChange={(e) => setHeight(parseInt(e.detail.value!))} />
              </IonItem>
              <IonItem>
                <IonLabel>Border</IonLabel>
                <IonCheckbox checked={hasBorder} onIonChange={(e) => setHasBorder(e.detail.checked)} />
              </IonItem>
              {extraOptions}
              <IonItem>
                <IonLabel position="stacked">Embed Code</IonLabel>
                <IonLabel position="stacked">
                  <IonNote>
                    Press the button to copy this snippet and paste it into your HTML to embed the widget.
                  </IonNote>
                </IonLabel>
                <br />
              </IonItem>
              <IonItem>
                <IonTextarea
                  autoGrow
                  fill="solid"
                  value={snippet}
                  readonly
                  css={widgetCreatorCss.snippetTextarea}
                ></IonTextarea>
                <IonButton onClick={copyToClipboard} fill="clear" slot="end">
                  <IonIcon icon={copyOutline} slot="icon-only"></IonIcon>
                </IonButton>
              </IonItem>
            </IonCol>
            <IonCol sizeXs="12" sizeXl="6" css={widgetCreatorCss.previewSection}>
              <IonItem lines="none">
                <Title>Preview</Title>
              </IonItem>
              <IonItem lines="none">
                <iframe
                  src={updateIframeWhenSrcChanges ? iframeSrc : initialIFrameSrc}
                  width={width}
                  height={height}
                  title={iframeTitle}
                  style={iframeStyle}
                ></iframe>
              </IonItem>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonCardContent>
      <ToastMsg
        isOpen={showToast}
        type="success"
        onClose={() => setShowToast(false)}
        message="Embed code copied to clipboard"
      />
    </IonCard>
  );
};

export default WidgetCreator;
